import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { Approver } from '@/store/modules/OfficeAbsenceModule';
import { hubClient } from '@tfpl/api-client';
import { getConfig } from '@/config';

export interface Label {
  value: string;
  label: string;
}

export interface User {
  id: number;
  uuid: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  positions: hubClient.PositionsDto[];
  isActive: boolean;
  isPublic: boolean;
  primaryOfficeAbsenceApprover?: Approver;
  slackAvatarUrl?: string;
  confirmSecurityTFDate: string;
  responsiblePerson: hubClient.MinimalCollaboratorDto;
  notifyResponsiblePersonAboutBirthday: boolean;
  positionOverride: string;
  labels: Label[];
}

@Module({ name: 'UserModule', store, dynamic: true, namespaced: true })
export class UserModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _users: User[] = [];

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get users(): User[] {
    return this._users;
  }

  get activeUsers(): User[] {
    return this._users.filter((user) => user.isActive);
  }

  get userByUuid() {
    return (uuid: string): User | null => {
      const res = this._users.filter((user) => user.uuid === uuid);
      if (res.length == 0) {
        return null;
      }
      return res[0];
    };
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  clearUsers(): void {
    this._users = [];
  }

  @Mutation
  setUsers(users: User[]): void {
    this._users = users;
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchUsers({
    action,
    subject,
    worklogsAuditorUuid,
    collaboratorUuid,
    responsiblePersonUuid,
    onlyWithWorkingTimeRecords,
    workEndMaxDaysOld,
  }: {
    action?: string;
    subject?: string;
    worklogsAuditorUuid?: string;
    collaboratorUuid?: string;
    responsiblePersonUuid?: string;
    onlyWithWorkingTimeRecords?: boolean;
    workEndMaxDaysOld?: number;
  }): Promise<void> {
    this.setLoading(true);

    try {
      const { data: response } = await hubClient.V3CollaboratorsService.getManyV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query: {
          permissionAction: action,
          permissionSubject: subject,
          limit: 999,
          offset: 0,
          worklogsAuditorUuid,
          collaboratorUuid,
          responsiblePersonUuid,
          onlyWithWorkingTimeRecords,
          workEndMaxDaysOld,
        },
        throwOnError: true,
      });
      const users: User[] = response.data.map((item) => {
        return {
          id: item.id,
          uuid: item.uuid,
          firstName: item.firstName,
          lastName: item.lastName,
          fullName: `${item.firstName} ${item.lastName}`,
          email: item.email,
          positions: item.positions,
          isActive: item.isActive,
          isPublic: item.isPublic,
          slackAvatarUrl: item.slackAvatarUrl,
          confirmSecurityTFDate: item.confirmSecurityTFDate,
          responsiblePerson: item.responsiblePerson,
          notifyResponsiblePersonAboutBirthday: item.notifyResponsiblePersonAboutBirthday,
          positionOverride: item.positionOverride,
          labels: item.labels
            ? item.labels.map((label) => ({
                value: label,
                label,
              }))
            : [],
          primaryOfficeAbsenceApprover: item.primaryOfficeAbsenceApprover
            ? {
                id: item.primaryOfficeAbsenceApprover.id,
                uuid: item.primaryOfficeAbsenceApprover.uuid,
                fullName: `${item.primaryOfficeAbsenceApprover.firstName} ${item.primaryOfficeAbsenceApprover.lastName}`,
                avatarUrl: item.slackAvatarUrl,
                priority: true,
              }
            : undefined,
        };
      });
      this.setUsers(users);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(UserModule);
